import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { CSSProperties } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LinkBox, LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import {
  BrandS,
  HeadlineM,
  LabelL,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { ScarcityLabel } from '@sorare/core/src/components/collections/ScarcityLabel';

import { useCardCollectionData } from '#src/components/collections/CollectionPreview/useCardCollectionData.ts';

import type { EmptyCollection_cardCollection } from './__generated__/index.graphql.ts';
import inSeasonBackground from './assets/in-season.webp';

const StyledLinkBox = styled(LinkBox)`
  transition: transform 0.2s ease-in-out;

  &:hover:not(.snapshotted),
  &:focus:not(.snapshotted) {
    color: inherit;
    transform: scale(1.02);
  }
`;
const EmptyCollectionRoot = styled(Vertical).attrs({ center: true })`
  height: 100%;
  background:
    linear-gradient(
      0deg,
      #000 0%,
      rgba(0, 0, 0, 0.58) 71.75%,
      rgba(0, 0, 0, 0) 100%
    ),
    linear-gradient(
      180deg,
      rgba(var(--collection-color-rgb), 0.2) 0%,
      rgba(var(--collection-color-rgb), 0.2) 100%
    ),
    var(--background-image) center / cover no-repeat;

  padding: calc(6 * var(--unit)) var(--double-unit);
  border-radius: var(--double-unit);
`;

const Logo = styled.img`
  width: 80px;
  height: 80px;
  object-fit: contain;
`;

type Props = {
  cardCollection: EmptyCollection_cardCollection;
  to?: string;
};

export const EmptyCollection = ({ cardCollection, to }: Props) => {
  const { rarity, team, snapshotted, inSeason } = cardCollection;

  const { pictureUrl, name } = team || {};

  const { displayedStartYear, displayedEndYear, backgroundColor, rgbColor } =
    useCardCollectionData(cardCollection);
  return (
    <StyledLinkBox className={classNames({ snapshotted })}>
      <EmptyCollectionRoot
        style={
          {
            '--background-image': inSeason
              ? `url(${inSeasonBackground})`
              : backgroundColor,
            '--collection-color-rgb': rgbColor || 'var(--c-rgb-black)',
            '--collection-color': backgroundColor || 'var(--c-black)',
          } as CSSProperties
        }
      >
        {!!pictureUrl && <Logo alt={name} src={pictureUrl} />}
        <Vertical gap={3} center>
          <Vertical gap={0} center className="text-center">
            <LabelS>
              <ScarcityLabel scarcity={rarity ?? Rarity.custom_series} />{' '}
            </LabelS>
            <HeadlineM>{name}</HeadlineM>
            <BrandS
              as="span"
              bold
              className={classNames({
                'text-gradient-color': inSeason,
              })}
              style={
                {
                  '--text-gradient-color': `linear-gradient(
                          45deg,
                          #f8d3da,
                          #b3a9f4 28%,
                          #fbe9fb 54%,
                          #4f94fd 100%
                          )`,
                } as CSSProperties
              }
            >
              {displayedStartYear}
              {!!displayedEndYear && `-${displayedEndYear}`}
            </BrandS>
            {!!snapshotted && (
              <LabelL color="var(--c-nd-600)">
                <FormattedMessage
                  id="CollectionPreview.noCollection"
                  defaultMessage="No Collection"
                />
              </LabelL>
            )}
          </Vertical>
          {!snapshotted && (
            <LinkOverlay as={Button} size="small" color="quaternary" to={to}>
              <FormattedMessage
                id="CollectionPreview.emptyCollectionCta"
                defaultMessage="Start Collection"
              />
            </LinkOverlay>
          )}
        </Vertical>
      </EmptyCollectionRoot>
    </StyledLinkBox>
  );
};

EmptyCollection.fragments = {
  cardCollection: gql`
    fragment EmptyCollection_cardCollection on CardCollection {
      slug
      rarity
      inSeason
      snapshotted
      team: anyTeam {
        slug
        name
        pictureUrl
      }
      ...useCardCollectionData_cardCollection
    }
    ${useCardCollectionData.fragments.cardCollection}
  ` as TypedDocumentNode<EmptyCollection_cardCollection>,
};
